import type { JSX } from 'react';
import { WhatsAppIcon } from '@components/icons/WhatsAppIcon';
import { landingCopy } from '@features/landing/data/landing.pt';
import { trackWhatsAppClick } from '@lib/analytics';
import { whatsappLink } from '@lib/whatsappLink';

const FLOATING_SOURCE_ID = 'floating-cta';

/**
 * Botao flutuante de WhatsApp (FAB) — LAYOUT-03.
 *
 * Sempre visivel no canto inferior direito, em qualquer ponto do scroll e em
 * qualquer resolucao. Circular, 56x56px (`h-14 w-14`) com toque generoso
 * (Apple HIG / WCAG 2.5.5 AAA = >=44x44).
 *
 * Spec visual:
 *  - `bg-brand-500` em rest, `hover:bg-brand-600` (mesma escala do Button primary)
 *  - `shadow-lg` verde-tinted (do INDEX.CSS)
 *  - `text-white` para que o `<WhatsAppIcon fill="currentColor" />` herde branco
 *  - Focus visible: ring brand-500 com offset neutral-50 (mesmo pattern do Button)
 *  - Posicao: `bottom-6 right-6` em mobile (24px), `lg:bottom-8 lg:right-8` em desktop
 *
 * Acessibilidade:
 *  - Renderizado como `<a>` (nao `<button>`) porque a acao e navegar para wa.me
 *  - `aria-label` descritivo de `landingCopy.floatingCta.ariaLabel`
 *  - Tab order: depois do conteudo principal (mesmo z-index alto, mas focus-visible
 *    funciona em qualquer ponto)
 *
 * CONV-03: passa `sourceId='floating-cta'` para distinguir do hero/navbar/final.
 *
 * Decisao de NAO-implementacao nesta phase: nenhuma logica de "ocultar quando
 * scroll esta no topo" ou "tooltip aparece apos N segundos" — PRD pede botao
 * SEMPRE VISIVEL (LAYOUT-03), e adicionar logica condicional adicionaria
 * complexidade sem ganho de conversao mensurado. Phase 6 pode revisitar.
 */
export const FloatingWhatsAppButton = (): JSX.Element => {
  const whatsappHref = whatsappLink(landingCopy.whatsapp.defaultMessage, FLOATING_SOURCE_ID);

  return (
    <a
      href={whatsappHref}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={landingCopy.floatingCta.ariaLabel}
      onClick={() => {
        trackWhatsAppClick(FLOATING_SOURCE_ID);
      }}
      className="fixed bottom-6 right-6 z-50 inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-500 text-white shadow-lg outline-none transition-colors duration-150 hover:bg-brand-600 hover:shadow-xl focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-50 active:translate-y-px lg:bottom-8 lg:right-8"
    >
      <WhatsAppIcon className="h-7 w-7" />
    </a>
  );
};
